// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Protected } from '@/adornments/components/protected';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { NumberTextField } from '@/components/number-text-field';
import { SerializableCurrency } from '@/currency/api/types';
import {
  ALL_CURRENCIES,
  APP_ALLOWED_CURRENCIES,
  IsoCurrencyCode,
} from '@/currency/constants/iso';
import {
  currencyOptionLabel,
  narrowCurrencySymbol,
} from '@/currency/utilities/format';
import { toCurrencyPasteResult } from '@/currency/utilities/paste';
import {
  getAmountPrecisionMessage,
  isZeroDecimal,
  parseCurrencyAmount,
} from '@/currency/utilities/validate';
import {
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { ClipboardEvent, useEffect, useState } from 'react';
import {
  Control,
  FieldPath,
  FieldPathByValue,
  FieldPathValue,
  FieldValues,
  useController,
  useWatch,
} from 'react-hook-form';
import { NumberFormatValues } from 'react-number-format';

interface PasteRejection {
  message: string;
  rejectedValue: unknown;
}

interface Props<Values extends FieldValues> {
  /**
   * Path of the nested currency object field (e.g. "price"). The component
   * binds `${name}.amount` and `${name}.isoCode`; it never registers the
   * parent path itself.
   */
  name: FieldPathByValue<Values, SerializableCurrency>;
  control: Control<Values>;

  /**
   * Default ISO code applied when the form does not seed one, replacing the
   * organization's default currency. Form-level defaultValues take
   * precedence over this prop.
   */
  defaultCurrencyCode?: IsoCurrencyCode;

  /**
   * Codes offered by the currency selector. Defaults to the app-allowed
   * currency set; the default ISO code must be in the list.
   */
  currencyCodeOptions?: readonly IsoCurrencyCode[];

  /**
   * Forces the currency to this ISO and disables the selector, leaving the
   * amount editable. Used to signal a currency that cannot be changed (e.g. a
   * line item with invoiced charges) without stripping the option list.
   */
  lockTo?: IsoCurrencyCode;

  /**
   * Tooltip shown on hover over the disabled currency selector, explaining why
   * it cannot be changed. Only rendered when the selector is disabled (via
   * lockTo or disabled).
   */
  disabledTooltip?: string;

  /**
   * Compact rendering for dense layouts. Combined with lockTo, the field shows
   * only the disambiguating leading currency symbol (e.g. "$", "CA$", "€") with
   * no end adornment — no dropdown and no ISO code — since the currency is fixed
   * and chosen elsewhere (e.g. a section header). Without lockTo the leading
   * symbol is the narrow form and the currency selector is still shown.
   */
  compact?: boolean;

  /**
   * Hides the end adornment entirely — no ISO code and no currency selector.
   * For dense layouts where the currency is already shown elsewhere and the
   * field should read as a plain amount input.
   */
  hideRightAdornment?: boolean;

  /**
   * Shows the admin-only "protected" shield before the end adornment, flagging
   * the field as restricted. Read/write gating is the caller's responsibility;
   * this is the visual indicator only.
   */
  protected?: boolean;

  /** Tooltip override for the protected shield; falls back to its default. */
  protectedTooltipMessage?: string;

  label?: string;
  fullWidth?: boolean;
  placeholder?: string;
  disabled?: boolean;

  /**
   * If true, the input will allow zero as a valid value.
   *
   * @default true
   */
  allowZero?: boolean;

  /**
   * If true, the input will allow negative values.
   *
   * @default false
   */
  allowNegative?: boolean;
}

/**
 * A money input validated by `currencyInputSchema`: a 2-decimal amount with
 * the selected currency's symbol as the start adornment and an ISO code
 * selector as the end adornment. Zero-decimal currencies (JPY, KRW, VND)
 * block decimal entry, and selecting one keeps any cents already typed and
 * flags them until the amount is fixed.
 */
export function FormCurrencyTextField<Values extends FieldValues>(
  props: Props<Values>
) {
  const { user } = useCurrentUser();
  const defaultCurrencyCode =
    props.defaultCurrencyCode ?? user.organization.defaultCurrency;

  const amountController = useController({
    control: props.control,
    name: `${props.name}.amount` as FieldPath<Values>,
    defaultValue: '' as FieldPathValue<Values, FieldPath<Values>>,
  });
  const isoController = useController({
    control: props.control,
    name: `${props.name}.isoCode` as FieldPath<Values>,
    defaultValue: defaultCurrencyCode as FieldPathValue<
      Values,
      FieldPath<Values>
    >,
  });

  // When locked, the displayed currency and the form value are both forced to
  // lockTo; the option list is left intact so the lock reads as a disabled
  // selection rather than a one-item dropdown.
  const currencyCode: IsoCurrencyCode =
    props.lockTo ?? isoController.field.value ?? defaultCurrencyCode;
  const zeroDecimal = isZeroDecimal(currencyCode);
  const selectableCurrencyCodes: readonly IsoCurrencyCode[] =
    props.currencyCodeOptions ?? APP_ALLOWED_CURRENCIES;

  const lockTo = props.lockTo;
  const isoValue = isoController.field.value;
  const isoOnChange = isoController.field.onChange;
  useEffect(() => {
    if (lockTo !== undefined && isoValue !== lockTo) {
      isoOnChange(lockTo);
    }
  }, [lockTo, isoValue, isoOnChange]);

  // An organization default the selector cannot offer is a broken currency
  // configuration; report it so we hear about the affected org.
  const orgDefaultUnavailable =
    props.defaultCurrencyCode === undefined &&
    props.lockTo === undefined &&
    !selectableCurrencyCodes.includes(user.organization.defaultCurrency);
  useEffect(() => {
    if (orgDefaultUnavailable) {
      captureException(
        new Error(
          `Organization default currency ${user.organization.defaultCurrency} is not an offered currency option`
        )
      );
    }
  }, [orgDefaultUnavailable, user.organization.defaultCurrency]);

  // A rejected paste leaves the value untouched, so its message lives here
  // rather than in form state. The message holds only while the field keeps
  // the value it was rejected against: an edit, a currency change or a form
  // reset (even to the same values) hands the field a new value and clears it.
  const currencyValue = useWatch({ control: props.control, name: props.name });
  const [pasteRejection, setPasteRejection] = useState<PasteRejection | null>(
    null
  );
  const pasteErrorMessage =
    pasteRejection !== null && pasteRejection.rejectedValue === currencyValue
      ? pasteRejection.message
      : null;

  const handleChangeAmount = (values: NumberFormatValues) => {
    amountController.field.onChange(values.value);
  };

  // A switch to a zero-decimal currency keeps the amount as typed; the
  // precision message below flags cents it cannot carry.
  const handleChangeCurrencyCode = (
    event: SelectChangeEvent<IsoCurrencyCode>
  ) => {
    // The select only renders registry MenuItems, so the value is an IsoCurrencyCode.
    isoController.field.onChange(event.target.value as IsoCurrencyCode);
  };

  // Every paste replaces the value: extract the amount (plain numeric text,
  // or a money shape like "€1,234.56" / "1000 EUR"), run the field's
  // constraints against it, and switch the currency when the paste uniquely
  // identifies one the selector offers. Any violation rejects the paste and
  // shows why, leaving the amount and currency unchanged.
  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    const pasted = toCurrencyPasteResult(event.clipboardData.getData('text'), {
      isoCode: currencyCode,
      selectableCurrencyCodes,
      lockTo: props.lockTo,
      allowNegative: props.allowNegative,
      allowZero: props.allowZero,
    });
    if (pasted.kind === 'rejected') {
      setPasteRejection({
        message: pasted.message,
        rejectedValue: currencyValue,
      });
      return;
    }

    if (pasted.isoCode !== currencyCode) {
      isoController.field.onChange(pasted.isoCode);
    }

    amountController.field.onChange(pasted.amount);
  };

  // A value finer than the currency's minor unit (a currency switch, or a
  // programmatic setValue) is flagged as soon as it appears, ahead of submit.
  const parsedAmount = parseCurrencyAmount(amountController.field.value ?? '');
  const precisionMessage =
    parsedAmount === null
      ? null
      : getAmountPrecisionMessage(parsedAmount, currencyCode);
  const fieldErrorMessage =
    pasteErrorMessage ??
    amountController.fieldState.error?.message ??
    isoController.fieldState.error?.message ??
    precisionMessage;
  const hasFieldError =
    pasteErrorMessage !== null ||
    amountController.fieldState.error !== undefined ||
    isoController.fieldState.error !== undefined ||
    precisionMessage !== null;

  // Compact + locked drops the end adornment, so the leading symbol must
  // disambiguate (full "CA$" over narrow "$"). Compact + unlocked keeps the
  // narrow symbol because the selector still shows the ISO code.
  const leadingSymbol =
    props.compact === true && props.lockTo === undefined
      ? narrowCurrencySymbol(currencyCode)
      : ALL_CURRENCIES[currencyCode].symbol;
  // Compact + locked has no selector to anchor a lock tooltip to, so when a
  // disabledTooltip is provided it moves to the leading symbol (the only visible
  // currency indicator).
  const compactLocked = props.compact === true && props.lockTo !== undefined;
  const startAdornment = (
    <InputAdornment position="start">
      {compactLocked && props.disabledTooltip !== undefined ? (
        <Tooltip title={props.disabledTooltip} placement="top" arrow>
          <span>{leadingSymbol}</span>
        </Tooltip>
      ) : (
        leadingSymbol
      )}
    </InputAdornment>
  );

  const selectorDisabled =
    props.disabled === true || props.lockTo !== undefined;
  const currencySelect = (
    <Select<IsoCurrencyCode>
      variant="standard"
      disableUnderline
      value={currencyCode}
      onChange={handleChangeCurrencyCode}
      renderValue={(value) => value}
      disabled={selectorDisabled}
      inputProps={{ 'aria-label': 'Currency' }}
      sx={{
        typography: 'body2',
        '& .MuiSelect-select': {
          paddingY: 0.25,
          paddingLeft: 0.75,
          paddingRight: 3,
          borderRadius: 1,
        },
        '& .MuiSelect-icon': { right: 0.75 },
        '& .MuiSelect-select:focus': {
          borderRadius: 1,
          backgroundColor: 'action.selected',
        },
      }}
    >
      {selectableCurrencyCodes.map((code) => (
        <MenuItem key={code} value={code}>
          {currencyOptionLabel(code)}
        </MenuItem>
      ))}
    </Select>
  );

  // A disabled selector swallows hover events, so wrap it in a span for the
  // tooltip to anchor to.
  const currencySelector =
    props.disabledTooltip !== undefined && selectorDisabled ? (
      <Tooltip title={props.disabledTooltip} placement="top" arrow>
        <span>{currencySelect}</span>
      </Tooltip>
    ) : (
      currencySelect
    );

  // Compact + locked shows no end adornment — the leading symbol already
  // identifies the fixed currency; every other mode keeps the currency dropdown
  // (disabled when locked).
  const shownEndAdornment =
    props.compact === true && props.lockTo !== undefined ? null : (
      <InputAdornment position="end">{currencySelector}</InputAdornment>
    );

  const protectedAdornment =
    props.protected === true ? (
      <Protected tooltipMessage={props.protectedTooltipMessage} />
    ) : null;

  const endAdornment =
    props.hideRightAdornment === true || shownEndAdornment === null ? (
      (protectedAdornment ?? undefined)
    ) : (
      <>
        {protectedAdornment}
        {shownEndAdornment}
      </>
    );

  return (
    <NumberTextField
      size="small"
      variant="outlined"
      name={amountController.field.name}
      value={amountController.field.value ?? ''}
      label={props.label}
      error={hasFieldError}
      helperText={fieldErrorMessage}
      onBlur={amountController.field.onBlur}
      inputRef={amountController.field.ref}
      onChange={handleChangeAmount}
      fullWidth={props.fullWidth}
      placeholder={props.placeholder ?? (zeroDecimal ? '0' : '0.00')}
      disabled={props.disabled}
      allowZero={props.allowZero}
      allowNegative={props.allowNegative ?? false}
      isCurrency
      maximumFractionDigits={zeroDecimal ? 0 : undefined}
      InputProps={{
        onPaste: handlePaste,
        startAdornment,
        endAdornment,
      }}
    />
  );
}
